import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { type ApiClient, UnauthorizedError } from '../api/client.js';
import type { RangeResponse, StatusResponse } from '../api/types.js';
import { useAuth } from '../auth/AuthContext.js';
import {
  dailyBucketsCsv,
  dailyFilename,
  hourlyBucketsCsv,
  hourlyFilename,
  topDaysCsv,
  topDaysFilename,
} from '../util/csv-builder.js';
import { addDays, localDayBounds, rangeBounds, todayString } from '../util/date-bounds.js';
import { triggerBlobDownload, triggerCsvDownload } from '../util/download-blob.js';
import { formatGallons } from '../util/format.js';
import { pickTopDays } from '../util/top-days.js';
import { Chart } from './Chart.js';
import styles from './Dashboard.module.css';
import { DateNavigator } from './DateNavigator.js';
import { EmptyState } from './EmptyState.js';
import { RangeSelector, RANGE_PRESETS, type RangePreset } from './RangeSelector.js';
import { StatusFooter } from './StatusFooter.js';
import { Top5Widget } from './Top5Widget.js';

interface DashboardProps {
  client: ApiClient;
}

// Earliest possible historical timestamp; the meter was installed mid-2026 so
// any time before that is safely "all data".
const EARLIEST_ISO = '2020-01-01T00:00:00.000Z';

export function Dashboard({ client }: DashboardProps) {
  const { logout } = useAuth();
  const [status, setStatus] = useState<StatusResponse | undefined>(undefined);
  const [error, setError] = useState<string | undefined>(undefined);

  // Top chart: a single day, hourly buckets.
  const [day, setDay] = useState<string>(() => todayString());
  const [dayRange, setDayRange] = useState<RangeResponse | undefined>(undefined);
  const [dayLoading, setDayLoading] = useState(false);

  // Bottom chart: a span of days, daily buckets.
  const [preset, setPreset] = useState<RangePreset>(RANGE_PRESETS[1]!); // 30 days default
  const [rangeData, setRangeData] = useState<RangeResponse | undefined>(undefined);
  const [rangeLoading, setRangeLoading] = useState(false);

  // Only the All-Data download is async (it hits /api/export). The per-widget
  // downloads build CSV synchronously from data already in memory.
  const [downloadingAll, setDownloadingAll] = useState(false);

  // People counts state. `peopleCounts` mirrors what's persisted server-side
  // (refreshed when the daily-totals range changes). `pendingEdits` holds
  // values the user is currently typing/just typed; on every PUT success we
  // merge that date's pending value into peopleCounts. `savingDates` drives
  // the inline "Saving..." indicator on each row.
  const [peopleCounts, setPeopleCounts] = useState<Record<string, number>>({});
  const [pendingEdits, setPendingEdits] = useState<Record<string, number | undefined>>({});
  const [savingDates, setSavingDates] = useState<ReadonlySet<string>>(() => new Set());
  const debounceTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  const handleAuthError = useCallback(
    (err: unknown): void => {
      if (err instanceof UnauthorizedError) {
        logout();
        return;
      }
      setError(err instanceof Error ? err.message : String(err));
    },
    [logout],
  );

  // Status: fetch once, refresh every 60s.
  useEffect(() => {
    let cancelled = false;
    const run = (): void => {
      void client
        .getStatus()
        .then((s) => {
          if (!cancelled) setStatus(s);
        })
        .catch(handleAuthError);
    };
    run();
    const id = setInterval(run, 60_000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [client, handleAuthError]);

  // Day chart: refetch when the selected day changes.
  useEffect(() => {
    let cancelled = false;
    setDayLoading(true);
    const { fromIso, toIso } = localDayBounds(day);
    void client
      .getRange(fromIso, toIso, 'hour')
      .then((r) => {
        if (!cancelled) setDayRange(r);
      })
      .catch((e) => {
        if (!cancelled) handleAuthError(e);
      })
      .finally(() => {
        if (!cancelled) setDayLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [client, day, handleAuthError]);

  // Range chart: refetch when the preset changes.
  useEffect(() => {
    let cancelled = false;
    setRangeLoading(true);
    const { fromIso, toIso } = rangeBounds(preset.days);
    void client
      .getRange(fromIso, toIso, 'day')
      .then((r) => {
        if (!cancelled) setRangeData(r);
      })
      .catch((e) => {
        if (!cancelled) handleAuthError(e);
      })
      .finally(() => {
        if (!cancelled) setRangeLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [client, preset, handleAuthError]);

  const topDays = useMemo(() => {
    if (rangeData === undefined) return [];
    return pickTopDays(rangeData.buckets, rangeData.timezone);
  }, [rangeData]);

  // Refetch persisted people counts whenever the daily-totals range changes.
  useEffect(() => {
    let cancelled = false;
    const today = todayString();
    const fromYyyymmdd = addDays(today, -(preset.days - 1));
    void client
      .getPeopleCounts(fromYyyymmdd, today)
      .then((resp) => {
        if (!cancelled) setPeopleCounts(resp.counts);
      })
      .catch((e) => {
        if (!cancelled) handleAuthError(e);
      });
    return () => {
      cancelled = true;
    };
  }, [client, preset, handleAuthError]);

  // Cancel any pending debounce timers on unmount.
  useEffect(() => {
    const timers = debounceTimers.current;
    return () => {
      for (const id of Object.values(timers)) clearTimeout(id);
    };
  }, []);

  const onChangePeopleCount = (yyyymmdd: string, peopleCount: number | undefined): void => {
    setPendingEdits((p) => ({ ...p, [yyyymmdd]: peopleCount }));

    // Reset the per-date debounce.
    const existing = debounceTimers.current[yyyymmdd];
    if (existing !== undefined) clearTimeout(existing);

    if (peopleCount === undefined) return;

    debounceTimers.current[yyyymmdd] = setTimeout(() => {
      setSavingDates((s) => {
        const next = new Set(s);
        next.add(yyyymmdd);
        return next;
      });
      void client
        .putPeopleCount(yyyymmdd, peopleCount)
        .then((resp) => {
          setPeopleCounts((c) => ({ ...c, [resp.yyyymmdd]: resp.peopleCount }));
          setPendingEdits((p) => {
            const { [yyyymmdd]: _drop, ...rest } = p;
            return rest;
          });
        })
        .catch(handleAuthError)
        .finally(() => {
          setSavingDates((s) => {
            const next = new Set(s);
            next.delete(yyyymmdd);
            return next;
          });
        });
    }, 500);
  };

  // Render-time merge of persisted + pending values.
  const visibleCounts: Record<string, number> = useMemo(() => {
    const merged: Record<string, number> = { ...peopleCounts };
    for (const [date, value] of Object.entries(pendingEdits)) {
      if (value === undefined) {
        delete merged[date];
      } else {
        merged[date] = value;
      }
    }
    return merged;
  }, [peopleCounts, pendingEdits]);

  // The "All Data" download still hits /api/export and returns the raw
  // READING# rows from the meter's history. The per-widget downloads below
  // build CSV in the browser from data already on the page, matching what
  // each widget displays.
  const downloadAll = async (): Promise<void> => {
    setDownloadingAll(true);
    try {
      const { filename, blob } = await client.downloadExport(
        EARLIEST_ISO,
        new Date().toISOString(),
      );
      triggerBlobDownload(filename, blob);
    } catch (e) {
      handleAuthError(e);
    } finally {
      setDownloadingAll(false);
    }
  };

  const downloadDay = (): void => {
    if (dayRange === undefined) return;
    triggerCsvDownload(hourlyFilename(day), hourlyBucketsCsv(dayRange.buckets));
  };

  const downloadRange = (): void => {
    if (rangeData === undefined) return;
    triggerCsvDownload(
      dailyFilename(preset.days),
      dailyBucketsCsv(rangeData.buckets, rangeData.timezone),
    );
  };

  const downloadTop5 = (): void => {
    if (topDays.length === 0) return;
    triggerCsvDownload(topDaysFilename(), topDaysCsv(topDays));
  };

  const dayHasData = (dayRange?.buckets ?? []).some((b) => b.intervalGallons > 0);
  const rangeHasData = (rangeData?.buckets ?? []).some((b) => b.intervalGallons > 0);

  return (
    <div className={styles.shell}>
      <header className={styles.header}>
        <h1 className={styles.title}>Camp Betz Water</h1>
        <button onClick={logout} className={styles.signout} type="button">
          Sign out
        </button>
      </header>

      {error !== undefined && (
        <div className={styles.errorBanner} role="alert">
          {error}
        </div>
      )}

      <section className={styles.heroPanel}>
        <div className={styles.heroStat}>
          <span className={styles.heroLabel}>Total water through the meter</span>
          <span className={styles.heroValue}>
            {status?.lastCumulativeGallons !== undefined
              ? formatGallons(status.lastCumulativeGallons)
              : '—'}
          </span>
          {status?.lastIngestAt !== undefined && (
            <span className={styles.heroAsOf}>as of {formatAsOf(status.lastIngestAt)}</span>
          )}
        </div>
        <button
          onClick={() => void downloadAll()}
          disabled={downloadingAll}
          className={styles.downloadAll}
          type="button"
        >
          {downloadingAll ? 'Downloading…' : 'Download All Data'}
        </button>
      </section>

      {/*
        The range preset drives three things: the Top 5 widget, the Daily totals
        chart, and the people-count fetch. It used to sit inside the Daily totals
        panel, below the Top 5 widget it silently governed -- changing it moved a
        widget further up the page with no visible cause.

        It is page-level now, and the panels it drives follow it immediately
        (Top 5, then Daily totals). "Today's usage by hour" is NOT driven by it and
        sits last, next to its own date navigator. That adjacency is what conveys
        the scope, which is why no explanatory caption is needed here -- keep the
        order intact and the control stays honest.
      */}
      <section className={styles.rangeBar} aria-label="Date range">
        <span className={styles.rangeBarLabel}>Range</span>
        <RangeSelector value={preset} onChange={setPreset} />
      </section>

      <Top5Widget
        days={topDays}
        peopleCounts={visibleCounts}
        savingDates={savingDates}
        onSelectDate={setDay}
        onChangePeopleCount={onChangePeopleCount}
        onDownload={downloadTop5}
        downloading={false}
      />

      <section className={styles.panel}>
        <header className={styles.panelHeader}>
          <h2 className={styles.panelTitle}>Daily totals</h2>
          <button
            onClick={downloadRange}
            disabled={rangeData === undefined}
            className={styles.panelDownload}
            type="button"
          >
            Download Data
          </button>
        </header>
        <div className={styles.chartArea}>
          {rangeLoading && rangeData === undefined ? (
            <EmptyState message="Loading…" />
          ) : rangeData === undefined || !rangeHasData ? (
            <EmptyState message="No usage recorded in this range." />
          ) : (
            <Chart
              buckets={rangeData.buckets}
              granularity={rangeData.granularity}
              timezone={rangeData.timezone}
            />
          )}
        </div>
      </section>

      <section className={styles.panel}>
        <header className={styles.panelHeader}>
          <h2 className={styles.panelTitle}>Today&rsquo;s usage by hour</h2>
          <button
            onClick={downloadDay}
            disabled={dayRange === undefined}
            className={styles.panelDownload}
            type="button"
          >
            Download Data
          </button>
        </header>
        <div className={styles.controls}>
          <DateNavigator value={day} onChange={setDay} />
        </div>
        <div className={styles.chartArea}>
          {dayLoading && dayRange === undefined ? (
            <EmptyState message="Loading…" />
          ) : dayRange === undefined || !dayHasData ? (
            <EmptyState message="No usage recorded for this day." />
          ) : (
            <Chart
              buckets={dayRange.buckets}
              granularity={dayRange.granularity}
              timezone={dayRange.timezone}
            />
          )}
        </div>
      </section>

      <StatusFooter status={status} />
    </div>
  );
}

function formatAsOf(iso: string): string {
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZone: 'America/New_York',
    timeZoneName: 'short',
  }).format(new Date(iso));
}

